import React, { createContext, useCallback, useContext, useMemo } from "react";
import type { FileWithPath } from "../FileDropzone/typings/FileObject.type";
import { useRemoteVisitorConsentDocuments } from "../../hooks/Remote/VisitConsent/useRemoteVisitorConsentDocuments";

export type VisitorConsentDocument = {
  id: string;
  order: number;
  title: string;
  /** Short helper shown in admin and above reader content when relevant. */
  description: string;
  /** Visitor-facing plain text shown when no attachment is configured. */
  body: string;
  fileName: string | null;
  /** Opens in new tab — storage URL from the API when configured. */
  fileViewerUrl: string | null;
};

export type VisitorConsentDocumentUpsertDraft = {
  title: string;
  description: string;
  body: string;
  order: number;
  file: FileWithPath | null;
  /** When updating, remove stored blob/file link if no replacement file was chosen. */
  dropExistingAttachment?: boolean;
};

type VisitorConsentDocumentsContextValue = {
  documents: VisitorConsentDocument[];
  orderedDocuments: VisitorConsentDocument[];
  isLoading: boolean;
  isMutating: boolean;
  addDocument: (draft: VisitorConsentDocumentUpsertDraft) => void;
  updateDocument: (id: string, draft: VisitorConsentDocumentUpsertDraft) => void;
  deleteDocument: (id: string) => void;
  deleteDocuments: (ids: string[]) => void;
};

const VisitorConsentDocumentsContext = createContext<VisitorConsentDocumentsContextValue | null>(
  null
);

/** Sort order: integer, at least 0, at most current document count (inclusive). */
export const clampVisitorConsentDocumentOrder = (order: number, maxInclusive: number): number => {
  if (!Number.isFinite(order)) {
    return 0;
  }
  const cap = Number.isFinite(maxInclusive) && maxInclusive > 0 ? Math.trunc(maxInclusive) : 0;
  const truncated = Math.trunc(order);
  if (truncated < 0) {
    return 0;
  }
  if (truncated > cap) {
    return cap;
  }
  return truncated;
};

export const VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN = 1;

/** Display order shown in admin UI: integer from 1 through document count (inclusive). */
export const clampVisitorConsentDocumentDisplayOrder = (
  displayOrder: number,
  maxDisplayInclusive: number
): number => {
  if (!Number.isFinite(displayOrder)) {
    return VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN;
  }
  const cap =
    Number.isFinite(maxDisplayInclusive) && maxDisplayInclusive > 0
      ? Math.trunc(maxDisplayInclusive)
      : VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN;
  const truncated = Math.trunc(displayOrder);
  if (truncated < VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN) {
    return VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN;
  }
  if (truncated > cap) {
    return cap;
  }
  return truncated;
};

export const storedOrderToDisplayOrder = (
  storedOrder: number,
  maxDisplayInclusive: number
): number => clampVisitorConsentDocumentDisplayOrder(storedOrder + 1, maxDisplayInclusive);

export const displayOrderToStoredOrder = (
  displayOrder: number,
  maxStoredInclusive: number
): number => clampVisitorConsentDocumentOrder(displayOrder - 1, maxStoredInclusive);

export const maxStoredOrderAmong = (documents: VisitorConsentDocument[]): number => {
  if (documents.length === 0) {
    return -1;
  }
  return Math.max(...documents.map(doc => doc.order));
};

/** Stored order suggested when appending a new document (after current maximum). */
export const nextStoredOrderForNewDocument = (documents: VisitorConsentDocument[]): number =>
  maxStoredOrderAmong(documents) + 1;

/** Inclusive stored-order cap while adding (one past current maximum). */
export const maxStoredOrderWhenAdding = (documents: VisitorConsentDocument[]): number =>
  Math.max(0, maxStoredOrderAmong(documents) + 1);

/** Inclusive stored-order cap while editing among existing documents. */
export const maxStoredOrderWhenEditing = (documents: VisitorConsentDocument[]): number =>
  Math.max(0, maxStoredOrderAmong(documents));

export const storedOrderCapToDisplayMax = (maxStoredInclusive: number): number =>
  Math.max(VISITOR_CONSENT_DOCUMENT_DISPLAY_ORDER_MIN, maxStoredInclusive + 1);

const sortedDocuments = (list: VisitorConsentDocument[]) =>
  [...list].sort((a, b) => {
    const byOrder = a.order - b.order;
    return byOrder !== 0 ? byOrder : a.id.localeCompare(b.id);
  });

const clampDraftOrder = (
  draft: VisitorConsentDocumentUpsertDraft,
  documents: VisitorConsentDocument[],
  editingId?: string
): VisitorConsentDocumentUpsertDraft => {
  const maxOrder = editingId
    ? maxStoredOrderWhenEditing(documents)
    : maxStoredOrderWhenAdding(documents);
  return {
    ...draft,
    order: clampVisitorConsentDocumentOrder(draft.order, maxOrder)
  };
};

export const VisitorConsentDocumentsProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { listQuery, createMutation, updateMutation, deleteMutation } =
    useRemoteVisitorConsentDocuments();

  const documents = listQuery.data ?? [];
  const orderedDocuments = useMemo(() => sortedDocuments(documents), [documents]);

  const isMutating =
    createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

  const addDocument = useCallback(
    (draft: VisitorConsentDocumentUpsertDraft) => {
      const clamped = clampDraftOrder(draft, documents);
      createMutation.mutate(clamped);
    },
    [createMutation, documents]
  );

  const updateDocument = useCallback(
    (id: string, draft: VisitorConsentDocumentUpsertDraft) => {
      const clamped = clampDraftOrder(draft, documents, id);
      updateMutation.mutate({ documentId: id, draft: clamped });
    },
    [documents, updateMutation]
  );

  const deleteDocument = useCallback(
    (id: string) => {
      if (documents.length <= 1) {
        return;
      }
      deleteMutation.mutate(id);
    },
    [deleteMutation, documents.length]
  );

  const deleteDocuments = useCallback(
    (ids: string[]) => {
      const idSet = new Set(ids.filter(Boolean));
      if (idSet.size === 0) {
        return;
      }
      const removable = documents.filter(doc => idSet.has(doc.id));
      if (documents.length - removable.length < 1) {
        return;
      }
      removable.forEach(doc => deleteMutation.mutate(doc.id));
    },
    [deleteMutation, documents]
  );

  const value = useMemo<VisitorConsentDocumentsContextValue>(
    () => ({
      documents,
      orderedDocuments,
      isLoading: listQuery.isLoading,
      isMutating,
      addDocument,
      updateDocument,
      deleteDocument,
      deleteDocuments
    }),
    [
      addDocument,
      deleteDocument,
      deleteDocuments,
      documents,
      isMutating,
      listQuery.isLoading,
      orderedDocuments,
      updateDocument
    ]
  );

  return (
    <VisitorConsentDocumentsContext.Provider value={value}>
      {children}
    </VisitorConsentDocumentsContext.Provider>
  );
};

export const useVisitorConsentDocuments = (): VisitorConsentDocumentsContextValue => {
  const context = useContext(VisitorConsentDocumentsContext);
  if (!context) {
    throw new Error(
      "useVisitorConsentDocuments must be used within VisitorConsentDocumentsProvider"
    );
  }
  return context;
};
